import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaNewspaper, FaSave, FaTimes, FaEye, FaImage, FaStar, FaCalendarAlt,
  FaBullhorn, FaGraduationCap, FaLaptop, FaBible, FaFire, FaMapMarkerAlt
} from 'react-icons/fa';
import noticiasMuralService from '../../services/noticiasMuralService';

// Mapeamento de ícones para seções
const iconMap = {
  FaStar: FaStar,
  FaCalendarAlt: FaCalendarAlt,
  FaBullhorn: FaBullhorn,
  FaLaptop: FaLaptop,
  FaGraduationCap: FaGraduationCap,
  FaBible: FaBible,
  FaFire: FaFire,
  FaNewspaper: FaNewspaper,
};

/**
 * Converte uma data ISO/string do banco para formato datetime-local (YYYY-MM-DDTHH:mm)
 * Tratando corretamente o timezone
 */
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Criar objeto Date a partir da string
    const date = new Date(dateString);
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) return '';
    
    // Formatar para datetime-local no timezone local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

/**
 * Formulário de criação/edição de notícia
 */
const NoticiaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingNoticia, setLoadingNoticia] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [secoes, setSecoes] = useState([]);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    titulo: '',
    subtitulo: '',
    conteudo: '',
    categoria_id: '',
    imagem_capa: '',
    status: 'rascunho',
    destaque: false,
    data_publicacao: '',
    data_expiracao: '',
    publico_alvo: 'todos',
    secoes: [] // Array de IDs das seções selecionadas
  });

  // Carregar categorias e seções
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const [categoriasRes, secoesRes] = await Promise.all([
          noticiasMuralService.listarCategorias(),
          noticiasMuralService.listarSecoes()
        ]);
        
        if (categoriasRes.success) {
          setCategorias(categoriasRes.data);
        }
        if (secoesRes.success) {
          setSecoes(secoesRes.data);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    carregarDados();
  }, []);

  // Carregar notícia se editando
  useEffect(() => {
    if (isEditing) {
      const carregarNoticia = async () => {
        try {
          setLoadingNoticia(true);
          const response = await noticiasMuralService.buscarPorId(id);
          if (response.success) {
            const noticia = response.data;
            setFormData({
              titulo: noticia.titulo || '',
              subtitulo: noticia.subtitulo || '',
              conteudo: noticia.conteudo || '',
              categoria_id: noticia.categoria_id || '',
              imagem_capa: noticia.imagem_capa || '',
              status: noticia.status || 'rascunho',
              destaque: noticia.destaque === 1,
              data_publicacao: formatDateForInput(noticia.data_publicacao),
              data_expiracao: formatDateForInput(noticia.data_expiracao),
              publico_alvo: noticia.publico_alvo || 'todos',
              secoes: noticia.secoes ? noticia.secoes.map(s => s.id) : []
            });
          }
        } catch (error) {
          console.error('Erro ao carregar notícia:', error);
          setError('Erro ao carregar notícia');
        } finally {
          setLoadingNoticia(false);
        }
      };
      carregarNoticia();
    }
  }, [id, isEditing]);

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handler para checkbox de seções
  const handleSecaoChange = (secaoId) => {
    setFormData(prev => {
      const secoes = prev.secoes.includes(secaoId)
        ? prev.secoes.filter(id => id !== secaoId)
        : [...prev.secoes, secaoId];
      return { ...prev, secoes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.titulo.trim()) {
      setError('O título é obrigatório');
      return;
    }
    if (!formData.conteudo.trim()) {
      setError('O conteúdo é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const dados = {
        ...formData,
        categoria_id: formData.categoria_id || null,
        data_publicacao: formData.data_publicacao || null,
        data_expiracao: formData.data_expiracao || null
      };

      let response;
      if (isEditing) {
        response = await noticiasMuralService.atualizar(id, dados);
      } else {
        response = await noticiasMuralService.criar(dados);
      }

      if (response.success) {
        navigate('/mural/gerenciar');
      } else {
        setError(response.message || 'Erro ao salvar notícia');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setError('Erro ao salvar notícia. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Salvar e publicar
  const handleSalvarEPublicar = async () => {
    setFormData(prev => ({ ...prev, status: 'publicado' }));
    // O submit será acionado após o state atualizar
    setTimeout(() => {
      document.getElementById('noticia-form').requestSubmit();
    }, 100);
  };

  if (loadingNoticia) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="bg-white rounded-lg p-6 space-y-4">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FaNewspaper className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editar Notícia' : 'Nova Notícia'}
              </h1>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Atualize as informações da notícia' : 'Preencha os dados para criar uma nova notícia'}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate('/mural/gerenciar')}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <FaTimes className="w-4 h-4" />
            Cancelar
          </button>
        </div>

        {/* Erro */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Formulário */}
        <form id="noticia-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Informações básicas */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h2>
            
            <div className="space-y-4">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  placeholder="Digite o título da notícia"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Subtítulo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtítulo / Resumo
                </label>
                <input
                  type="text"
                  name="subtitulo"
                  value={formData.subtitulo}
                  onChange={handleChange}
                  placeholder="Uma breve descrição para aparecer na listagem"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Categoria */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <select
                    name="categoria_id"
                    value={formData.categoria_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Selecione uma categoria</option>
                    {categorias.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Público-alvo
                  </label>
                  <select
                    name="publico_alvo"
                    value={formData.publico_alvo}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="todos">Todos os usuários</option>
                    <option value="administradores">Apenas Administradores</option>
                    <option value="operadores">Operadores e acima</option>
                    <option value="coordenadores">Coordenadores</option>
                  </select>
                </div>
              </div>

              {/* Imagem de capa */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FaImage className="inline-block w-4 h-4 mr-1" />
                  URL da Imagem de Capa
                </label>
                <input
                  type="url"
                  name="imagem_capa"
                  value={formData.imagem_capa}
                  onChange={handleChange}
                  placeholder="https://exemplo.com/imagem.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {formData.imagem_capa && (
                  <div className="mt-2">
                    <img 
                      src={formData.imagem_capa} 
                      alt="Preview" 
                      className="h-32 object-cover rounded-lg"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Conteúdo</h2>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg"
              >
                <FaEye className="w-4 h-4" />
                {showPreview ? 'Editar' : 'Preview'}
              </button>
            </div>

            {showPreview ? (
              <div 
                className="prose max-w-none min-h-[200px] p-4 border border-gray-200 rounded-lg bg-gray-50"
                dangerouslySetInnerHTML={{ __html: formData.conteudo || '<p class="text-gray-400">Nenhum conteúdo</p>' }}
              />
            ) : (
              <div>
                <textarea
                  name="conteudo"
                  value={formData.conteudo}
                  onChange={handleChange}
                  placeholder="Digite o conteúdo da notícia (suporta HTML)"
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Dica: Você pode usar tags HTML como &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
                </p>
              </div>
            )}
          </div>

          {/* Configurações de publicação */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Configurações de Publicação</h2>
            
            <div className="space-y-4">
              {/* Status e Destaque */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="rascunho">Rascunho</option>
                    <option value="publicado">Publicado</option>
                    <option value="arquivado">Arquivado</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="destaque"
                      checked={formData.destaque}
                      onChange={handleChange}
                      className="w-5 h-5 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <FaStar className="w-5 h-5 text-amber-500" />
                    <span className="text-sm text-gray-700">Marcar como destaque (aparece no topo)</span>
                  </label>
                </div>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FaCalendarAlt className="inline-block w-4 h-4 mr-1" />
                    Data de Publicação (agendar)
                  </label>
                  <input
                    type="datetime-local"
                    name="data_publicacao"
                    value={formData.data_publicacao}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Deixe vazio para publicar imediatamente
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FaCalendarAlt className="inline-block w-4 h-4 mr-1" />
                    Data de Expiração
                  </label>
                  <input
                    type="datetime-local"
                    name="data_expiracao"
                    value={formData.data_expiracao}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A notícia será ocultada após esta data
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seções de Exibição */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <FaMapMarkerAlt className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Onde Exibir no Portal</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Selecione em quais seções do portal esta notícia deve aparecer. Você pode selecionar múltiplas seções.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {secoes.map(secao => {
                const IconComponent = iconMap[secao.icone] || FaNewspaper;
                const isSelected = formData.secoes.includes(secao.id);
                
                return (
                  <label 
                    key={secao.id}
                    className={`
                      flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSecaoChange(secao.id)}
                      className="mt-1 w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <IconComponent 
                          className="w-4 h-4 flex-shrink-0" 
                          style={{ color: secao.cor }} 
                        />
                        <span className="font-medium text-gray-900 text-sm truncate">
                          {secao.nome}
                        </span>
                      </div>
                      {secao.descricao && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {secao.descricao}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            {formData.secoes.length === 0 && (
              <p className="mt-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ Nenhuma seção selecionada. A notícia só aparecerá na listagem geral e nas buscas.
              </p>
            )}

            {formData.secoes.length > 0 && (
              <div className="mt-4 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                ✓ Esta notícia aparecerá em <strong>{formData.secoes.length}</strong> seção(ões) do portal.
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div className="flex items-center justify-between bg-white rounded-lg shadow-sm p-4">
            <button
              type="button"
              onClick={() => navigate('/mural/gerenciar')}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>

            <div className="flex items-center gap-3">
              {/* Salvar como rascunho */}
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <FaSave className="w-4 h-4" />
                {loading ? 'Salvando...' : 'Salvar'}
              </button>

              {/* Salvar e publicar */}
              {formData.status !== 'publicado' && (
                <button
                  type="button"
                  onClick={handleSalvarEPublicar}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <FaSave className="w-4 h-4" />
                  {loading ? 'Publicando...' : 'Salvar e Publicar'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NoticiaForm;
